import { format, isAfter } from 'date-fns';
import { emsMainCodeList } from '../../constants/formConstants';
import {
  BHTransformedProduct,
  ContactsPackage,
  GroupCodes,
  GroupPackage,
  GroupsPackage,
  PlanCodePackage,
  ProductsPackage
} from '../../@types/Groups';

export const defaultMarketingContact: ContactsPackage = {
  email: '',
  first: '',
  last: '',
  type: 'Marketing'
};

export const defaultEmptyGroupCodes = {
  code: '',
  start_dt: '',
  end_dt: null
};

export const emptyPlanCode = {
  bpCodes: [],
  classCodes: [],
  code: ''
};

export const emptyGroup = {
  start_dt: null,
  end_dt: null,
  language: 'en',
  selectedPlanNumber: '',
  reg_type: 'hybrid',
  planCodes: [emptyPlanCode],
  products: {
    GM: { isEnabled: 'true', member_copay: 0, app_opt_out: false },
    DERM: { isEnabled: 'false', member_copay: 0 },
    BH: { isEnabled: 'false', BHINIT: 0, BHMD: 0, BHNPT: 0 },
    EMS: { isEnabled: 'false' },
    could_be_less: 'true'
  }
};

const emptyContact = {
  email: '',
  first: '',
  last: '',
  phone: ''
};

export class IncomingGroupDataModifier {
  public transform(data: GroupsPackage) {
    // @ts-ignore
    const { contacts, CreatedAt, UpdatedAt, start_dt, end_dt, groups, terminateReason, id, ...rest } = data;

    return {
      ...rest,
      ...this.dateTransformer({ start_dt, end_dt }),
      CreatedAt: CreatedAt ? format(new Date(CreatedAt), 'MM/dd/yyyy, hh:mm a') : CreatedAt,
      UpdatedAt: UpdatedAt ? format(new Date(UpdatedAt), 'MM/dd/yyyy, hh:mm a') : UpdatedAt,
      groups: this.groupTransform(groups),
      contacts: this.contactTransform(contacts)
    };
  }

  private toDate = (date: string | null) => (date ? new Date(date) : date);

  private checkIsEnabled = ({ start_dt, end_dt }: any) =>
    start_dt && (!end_dt || isAfter(new Date(end_dt), new Date())) ? 'true' : 'false';

  private getPlanCodes = (planCodes: PlanCodePackage[]) => {
    return planCodes.length
      ? planCodes.map((code: PlanCodePackage) => ({
          ...code,
          bpCodes: this.codeTransformer(code.bpCodes!),
          classCodes: this.codeTransformer(code.classCodes!)
        }))
      : [emptyPlanCode];
  };

  private dateTransformer = ({ start_dt = null, end_dt = null }: any) => ({
    start_dt: this.toDate(start_dt),
    end_dt: this.toDate(end_dt)
  });

  private groupTransform(groups: GroupPackage[]) {
    return groups
      ? groups.map((group: GroupPackage) => ({
          ...group,
          ...this.dateTransformer(group),
          products: this.productReorganizer(group.products),
          selectedPlanNumber: group.planCodes?.[0]?.code || '',
          planCodes: this.getPlanCodes(group.planCodes),
          ...(group.futureProducts ? { futureProducts: this.productReorganizer(group.futureProducts, true) } : {})
        }))
      : [];
  }

  private productTransform = (product = {}) => ({
    ...product,
    ...this.dateTransformer(product),
    isEnabled: this.checkIsEnabled(product)
  });

  private productReorganizer = (products: ProductsPackage[], futureFlag: boolean = false) => {
    const GM = products.find((product: ProductsPackage) => product.code === 'GM');
    const DERM = products.find((product: ProductsPackage) => product.code === 'DERM');
    const BH = products
      .filter((product: ProductsPackage) => product.code === 'BH')
      .reduce((acc, curr: ProductsPackage) => {
        const { member_copay, type, ...rest } = curr;
        acc = { ...acc, ...rest, [type!]: member_copay };
        return acc;
      }, {} as BHTransformedProduct);

    const baseProducts = {
      GM: {
        ...this.productTransform(GM),
        member_copay: GM?.member_copay || 0,
        app_opt_out: GM?.app_opt_out || false // app_opt_out is GM-specific
      },
      DERM: { ...this.productTransform(DERM), member_copay: DERM?.member_copay || 0 },
      BH: { ...this.productTransform(BH), BHINIT: BH?.BHINIT || 0, BHMD: BH?.BHMD || 0, BHNPT: BH?.BHNPT || 0 },
      could_be_less: products[0]?.could_be_less ? 'true' : 'false'
    };

    if (futureFlag) return baseProducts;

    const EMS = products.filter((product: ProductsPackage) => emsMainCodeList.includes(product.code))[0];
    return {
      ...baseProducts,
      EMS: { ...this.productTransform(EMS), code: 'EMS', isEfb: EMS?.type === 'EFB' || false }
    };
  };

  private contactTransform = (contacts: ContactsPackage[]) => {
    return contacts
      ? contacts.reduce((acc, contact: ContactsPackage) => {
          if (contact.type === 'Marketing') {
            acc[contact.type] = [...(acc[contact.type] || []), contact].flat();
          } else {
            acc[contact.type] = contact;
          }
          return acc;
        }, {})
      : {
          Account: { ...emptyContact, type: 'Account' },
          SAE: { ...emptyContact, type: 'SAE' }
        };
  };

  private codeTransformer = (fields: GroupCodes[]) =>
    (fields || []).map((field: GroupCodes) => ({ ...field, ...this.dateTransformer(field) }));
}
